import type { ViewContainerRef } from '@angular/core';
import { Injectable, inject } from '@angular/core';

import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import type { Observable } from 'rxjs';
import { combineLatest, map, of, switchMap } from 'rxjs';
import { take } from 'rxjs/operators';

import { AbilitiesSelectors } from '@core/abilities-data';
import { Ability } from '@core/abilities-types';
import { UserActions } from '@core/account-data';
import type { User } from '@core/account-types';
import { AnalyticsMode } from '@core/analytics-types';
import { FullCandidateSelectors } from '@core/candidate-data';
import type { ShareOnTheGoRequestPayload } from '@core/candidate-types';
import { ShareCandidatesAnalytics, ShareCandidatesAnalyticsTrigger } from '@core/candidate-util';
import { I18nService } from '@core/i18n-legacy';
import { InterviewEventsActions } from '@core/interview-events-data';
import { InterviewResultRequestActions } from '@core/interview-result-request-data';
import { JobsWrapperDataService } from '@core/offers-data';
import { OfferSimpleListItem, OfferKind } from '@core/offers-types';
import { TalentPoolsDataService } from '@core/talent-pools-data';
import type { TalentPool } from '@core/talent-pools-types';
import { DialogService, DialogWidth, NotificationsController } from '@core/user-interface-legacy';

import { ShareOnTheGoDialogComponent } from '../components/share-on-the-go-dialog/share-on-the-go-dialog.component';
import type {
  ShareOnTheGoDialogData,
  ShareOnTheGoDialogResult,
  UserAccessValue,
} from '../components/share-on-the-go-dialog/types';
import { AccessValue } from '../components/share-on-the-go-dialog/types';

type GrantAccess = ShareOnTheGoRequestPayload['grantAccess'][number];

@Injectable({ providedIn: 'root' })
export class ShareOnTheGoService {
  private readonly dialogService = inject(DialogService);
  private readonly store = inject(Store);
  private readonly talentPoolsDataService = inject(TalentPoolsDataService);
  private readonly jobsWrapperDataService = inject(JobsWrapperDataService);
  private readonly actions$ = inject(Actions);
  private readonly notificationsController = inject(NotificationsController);
  private readonly i18n = inject(I18nService);
  private readonly shareCandidatesAnalytics = inject(ShareCandidatesAnalytics);

  constructor() {
    this.errorOnEvaluationRequestFailure();
    this.trackAddTeamMembers();
    this.refreshAdminsAfterEvaluationRequest();
  }

  public openShareOnTheGoDialogIfPossible({
    shareOnTheGoDialogData,
    viewContainerRef,
  }: {
    shareOnTheGoDialogData: ShareOnTheGoDialogData;
    viewContainerRef: ViewContainerRef;
  }): Observable<ShareOnTheGoDialogResult | null | undefined> {
    return this.canOpenShareOnTheGoDialog$({ shareOnTheGoDialogData }).pipe(
      switchMap(canOpenDialog =>
        canOpenDialog
          ? this.usersWithoutAccessToCandidateAndOffer$(shareOnTheGoDialogData)
          : of([]),
      ),
      switchMap((usersWithoutAccess: User[]) => {
        if (!usersWithoutAccess.length) {
          return of(<ShareOnTheGoDialogResult>{
            userAccessValues: <UserAccessValue[]>[],
          });
        }

        return this.dialogService.openCustom(
          ShareOnTheGoDialogComponent,
          {
            width: DialogWidth.Small,
          },
          {
            ...shareOnTheGoDialogData!,
            usersToShareWith: usersWithoutAccess,
            allSelectedUsers: shareOnTheGoDialogData!.usersToShareWith,
          },
          viewContainerRef,
        ).afterClosed;
      }),
      take(1),
    );
  }

  public resultToRequestPayload({
    shareOnTheGoResult,
    offerId,
    talentPoolId,
    candidateId,
  }: {
    shareOnTheGoResult: ShareOnTheGoDialogResult;
    offerId?: number;
    talentPoolId?: number;
    candidateId: number;
  }): Observable<ShareOnTheGoRequestPayload> {
    const placementId$ = this.getPlacementId$({ candidateId, offerId, talentPoolId });
    const offer$ = offerId
      ? this.jobsWrapperDataService.getJobItemById$(offerId).pipe(take(1))
      : of(null);
    const talentPool$ = this.talentPoolsDataService.collection$().pipe(
      map(
        talentPools =>
          talentPools.find(talentPool => talentPool.id === (offerId || talentPoolId)) ?? null,
      ),
      take(1),
    );

    return combineLatest([placementId$, offer$, talentPool$]).pipe(
      map(([placementId, offer, talentPool]) =>
        this.createPayload({
          userAccessValues: shareOnTheGoResult.userAccessValues,
          placementId,
          offer,
          talentPool,
        }),
      ),
    );
  }

  private createPayload({
    userAccessValues,
    placementId,
    offer,
    talentPool,
  }: {
    userAccessValues: UserAccessValue[];
    placementId: number | null;
    offer: OfferSimpleListItem | null;
    talentPool: TalentPool | null;
  }): ShareOnTheGoRequestPayload {
    return {
      grantAccess: userAccessValues
        .filter(userAccessValue => userAccessValue.accessValue !== AccessValue.DontRequest)
        .map(userAccessValue => {
          const userId = userAccessValue.user.id;

          if (userAccessValue.accessValue === AccessValue.AllCandidatesInOffer) {
            if (offer && offer.kind === OfferKind.Job) {
              return this.createGrantAccessObject(userId, 'offerId', offer.id);
            }

            return this.createGrantAccessObject(userId, 'talentPoolId', talentPool?.id ?? null);
          }

          return this.createGrantAccessObject(userId, 'placementId', placementId);
        }),
    };
  }

  private createGrantAccessObject<K extends keyof Omit<GrantAccess, 'adminId'>>(
    userId: number,
    grantAccessProp: K,
    grantAccessPropValue: GrantAccess[K],
  ): GrantAccess {
    const base: GrantAccess = {
      adminId: userId,
      offerId: null,
      placementId: null,
      talentPoolId: null,
    };

    return {
      ...base,
      [grantAccessProp]: grantAccessPropValue,
    };
  }

  private usersWithoutAccessToCandidateAndOffer$(
    shareOnTheGoDialogData: ShareOnTheGoDialogData,
  ): Observable<User[]> {
    return this.store
      .select(
        FullCandidateSelectors.offerUsersGrouped({
          candidateId: shareOnTheGoDialogData!.candidateId,
          offerId: shareOnTheGoDialogData!.offerId,
          talentPoolId: shareOnTheGoDialogData!.talentPoolId,
        }),
      )
      .pipe(
        map(({ usersWithoutAccess }) =>
          usersWithoutAccess.filter(user => !user.roleAbilities.includes(Ability.ViewAllJobs)),
        ),
        map(usersWithoutAccess =>
          shareOnTheGoDialogData!.usersToShareWith.filter(userToShareWith =>
            usersWithoutAccess.find(
              userWithoutAccess => userWithoutAccess.id === userToShareWith.id,
            ),
          ),
        ),
        take(1),
      );
  }

  private getPlacementId$({
    candidateId,
    offerId,
    talentPoolId,
  }: {
    candidateId: number;
    offerId?: number;
    talentPoolId?: number;
  }): Observable<number | null> {
    return this.store
      .select(FullCandidateSelectors.byIdWithReferences(candidateId))
      .pipe(
        map(
          candidate =>
            candidate?.placements?.find(
              placement =>
                (placement.offerId || placement.talentPoolId) === (offerId || talentPoolId),
            )?.id ?? null,
        ) ?? null,
        take(1),
      );
  }

  private canOpenShareOnTheGoDialog$({
    shareOnTheGoDialogData,
  }: {
    shareOnTheGoDialogData: ShareOnTheGoDialogData;
  }): Observable<boolean> {
    return this.store.select(AbilitiesSelectors.hasOne(Ability.ShareCandidates)).pipe(
      map(
        canShareCandidates =>
          canShareCandidates &&
          ((shareOnTheGoDialogData?.offerId || shareOnTheGoDialogData?.talentPoolId) ?? -1) > 0 &&
          !!shareOnTheGoDialogData?.usersToShareWith.length,
      ),
      take(1),
    );
  }

  private errorOnEvaluationRequestFailure(): void {
    this.actions$
      .pipe(ofType(InterviewResultRequestActions.createOnePessimisticFailed))
      .subscribe(() =>
        this.notificationsController.error(
          this.i18n.translate('app.common.error_something_went_wrong'),
        ),
      );
  }

  private trackAddTeamMembers(): void {
    this.actions$
      .pipe(
        ofType(
          InterviewEventsActions.scheduleSuccess,
          InterviewResultRequestActions.createOnePessimisticSuccess,
        ),
      )
      .subscribe(action => {
        if (action.payload.grantAccess?.length) {
          const isScheduleInterview = action.type === InterviewEventsActions.scheduleSuccess.type;

          this.shareCandidatesAnalytics.trackAddTeamMembers({
            teamMembers: action.payload.grantAccess!.length,
            trigger: isScheduleInterview
              ? ShareCandidatesAnalyticsTrigger.ScheduleInterview
              : ShareCandidatesAnalyticsTrigger.EvaluationRequest,
            mode: AnalyticsMode.Single,
          });
        }
      });
  }

  private refreshAdminsAfterEvaluationRequest(): void {
    this.actions$
      .pipe(ofType(InterviewResultRequestActions.createOnePessimisticSuccess))
      .subscribe(action => {
        if (action.payload.grantAccess?.length) {
          this.store.dispatch(UserActions.fetchCollection());
        }
      });
  }
}
